import axios from 'axios';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
import { ClientLocal } from '../../views/Restaurant/ClientManagement/Clients/types/client.types';

const BASE_URL = NAMESPACES.clients_api.local_clients;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
}));

// Get all clients for a specific local/restaurant
export const getClientsByLocal = async (localId: number): Promise<{ data: ClientLocal[] }> => {
  return retryWithBackoff(() => api.get(`/GetClientsByLocal/${localId}`));
};

// Get all locals/restaurants for a specific client
export const getLocalsByClient = async (clientId: number) => {
  return retryWithBackoff(() => api.get(`/GetLocalsByClient/${clientId}`));
};

// Add a client to a local using client code
export const addClientToLocal = async (code: string, localId: number): Promise<ClientLocal> => {
  return retryWithBackoff(() => api.post('/AddClientToLocal', {
    code,
    local_id: localId
  }));
};

// Add a local to a client using local code (or local_id directly)
export const addLocalToClient = async (clientId: number, localId: number) => {
  // Generate a simple code format: LOCAL-{id}
  const code = `LOCAL-${localId}`;
  
  return retryWithBackoff(() => api.post('/AddLocalToClient', {
    code,
    client_id: clientId
  }));
};

/**
 * Search clients by name, email, or phone number for a specific local
 * 
 * @param localId - The local/restaurant ID
 * @param searchTerm - The search query string
 * @param searchField - The field to search in: 'name', 'email', or 'phone'
 * @returns Array of matching clients
 */
export const searchClientsByLocal = async (
  localId: number,
  searchTerm: string,
  searchField: 'name' | 'email' | 'phone' = 'name'
): Promise<{ data: ClientLocal[] }> => {
  const response: any = await retryWithBackoff(() => api.get(`/GetClientsByLocal/${localId}`, {
    params: {
      search: searchTerm,
      field: searchField
    }
  }));
  
  // If backend doesn't support search params, filter client-side
  let clients = response.data;
  
  if (searchTerm && searchTerm.trim() !== '') {
    const searchLower = searchTerm.toLowerCase().trim();
    clients = clients.filter((client: ClientLocal) => {
      switch (searchField) {
        case 'email':
          return client.email?.toLowerCase().includes(searchLower);
        case 'phone':
          return client.phone_number?.toLowerCase().includes(searchLower);
        case 'name':
        default:
          // Search in username, middle_name, and last_name
          const fullName = `${client.username || ''} ${client.middle_name || ''} ${client.last_name || ''}`.toLowerCase();
          return fullName.includes(searchLower);
      }
    });
  }
  
  return { data: clients };
};

/**
 * @ns.route('/RemoveClientFromLocal/<int:local_id>/<int:client_id>')
@ns.param('local_id', 'The local identifier')
@ns.param('client_id', 'The client identifier')
class RemoveClientFromLocal(Resource):
    """
    Endpoint for removing the association between a local and a client.
    
    Permanently deletes the LocalClient record, removing all visit history,
    spending statistics, and membership information for this specific local.
    """
    
    @ns.doc('remove_client_from_local',
            description='Remove a client from a local. This permanently deletes the '
                       'LocalClient record including all visit history, spending stats, '
                       'and membership information. If the client is added back later, '
                       'they will start from scratch with default values.',
            responses={
                200: 'Client removed successfully',
                401: 'Unauthorized - Invalid or missing JWT token',
                404: 'Client, local, or association not found',
                500: 'Internal server error'
            })
    @ns.marshal_with(deletion_response_model)
    @jwt_required()
    def delete(self, local_id, client_id):
        """
        Remove a client from a local.
        
        Requires JWT authentication.
        
        URL Parameters:
        - local_id: ID of the local/restaurant
        - client_id: ID of the client to remove
        
        Returns:
            200: Success message with local and client details
            404: Client, local, or association not found
        
        WARNING: This action is irreversible. All visit history, spending stats,
        and membership information for this local will be permanently deleted.
        """
        try:
            print(f"[NAMESPACE] RemoveClientFromLocal endpoint called with local_id: {local_id}, client_id: {client_id}")
            helper = LocalClientHelper()
            success, data, status = helper.removeClientFromLocal(local_id, client_id)
            print(f"[NAMESPACE] Helper returned - success: {success}, status: {status}")
            return data, status
        except Exception as e:
            print(f"[NAMESPACE ERROR] Unexpected error in RemoveClientFromLocal: {type(e).__name__}: {str(e)}")
            import traceback
            traceback.print_exc()
            return {'message': 'Error interno al remover cliente'}, 500
 */

export const removeClientFromLocal = async (localId: number, clientId: number) => {
  return retryWithBackoff(() => api.delete(`/RemoveClientFromLocal/${localId}/${clientId}`));
};
